import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { useAtom, useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import { pollsAtom, useStartPoll } from '~shared/polling';
import { useQuizContext } from '~teacher/components/QuizEditor/contexts/QuizContextProvider';
import {
  generateQuestionAtom,
  promptAtom,
} from '~teacher/components/QuizEditor/GenerateQuestionDialog/atom';
import { useAiErrorHandler } from '~teacher/hook/useAiErrorHandler';
import { selectEditingQuiz } from '~teacher/slices/quiz/quizSelectors';

import { useQuestionGenerator } from './context';
import { checkIsPromptEmpty, checkIsQuestionTypeEmpty } from './utils';

interface UseHandleGenerateQuestionsParams {
  fileKey: string | null;
  fileType: string | null;
  fileSize: number | null;
}

export function useHandleGenerateQuestions({
  fileKey,
  fileType,
  fileSize,
}: UseHandleGenerateQuestionsParams) {
  const handleAiError = useAiErrorHandler({
    featureTag: 'ai-generate-questions',
  });
  const questionGenerator = useQuestionGenerator();
  const { startPoll } = useStartPoll();
  const { quizId, workspaceId, workspaceQuizId } = useQuizContext();
  const quiz = useSelector(selectEditingQuiz);
  const socNumber = quiz?.socNumber;
  const polls = useAtomValue(pollsAtom);

  const [
    {
      [QuestionTypes.MultipleChoice]: multipleChoice,
      [QuestionTypes.TrueFalse]: trueFalse,
      [QuestionTypes.FreeResponse]: shortAnswers,
      questionCount,
      includeExplanation,
    },
  ] = useAtom(generateQuestionAtom);
  const [prompt] = useAtom(promptAtom);

  return () => {
    // Check if there's already an active poll
    const hasActivePoll = polls.some(
      (poll) =>
        poll.type === 'ai-questions' &&
        (poll.metadata as any)?.quizId === quizId
    );

    if (hasActivePoll) {
      return;
    }

    if (
      checkIsQuestionTypeEmpty({ multipleChoice, trueFalse, shortAnswers }) ||
      (checkIsPromptEmpty(prompt) && !fileKey)
    ) {
      toast.critical(translate('Required field(s) missing'));
      return;
    }

    questionGenerator.mutate(
      {
        questionCount: questionCount,
        includeExplanation: includeExplanation,
        questionTypes: [
          ...(multipleChoice ? ['MultipleChoice' as const] : []),
          ...(trueFalse ? ['TrueFalse' as const] : []),
          ...(shortAnswers ? ['FreeResponse' as const] : []),
        ],
        ...(prompt.trim() ? { topic: prompt } : {}),
        ...(fileKey ? { fileKey } : {}),
      },
      {
        onSuccess: (data) => {
          // Check if response is async (file-based generation)
          if ('async' in data && data.async) {
            // Start polling for the async job with analytics metadata
            startPoll({
              id: `questions-${data.requestId}`,
              type: 'ai-questions',
              metadata: {
                requestId: data.requestId,
                context: 'generate',
                quizId: quizId,
                socNumber: socNumber,
                workspaceId: workspaceId,
                workspaceQuizId: workspaceQuizId,
                analyticsMetadata: {
                  numQuestions: questionCount,
                  multipleChoice,
                  trueFalse,
                  shortAnswer: shortAnswers,
                  includeExplanation,
                  promptLength: prompt.length,
                  fileUploaded: !!fileKey,
                  fileUploadType: fileType || null,
                  fileUploadSizeBytes: fileSize || null,
                  filesUploadedCount: fileKey ? 1 : 0,
                },
              },
            });
          }
        },
        onError: handleAiError,
      }
    );
  };
}
